import React, { useRef, useCallback, useState, FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { approxFormatBinaryNumber } from 'cityads-v3/utils/textUtils'

import AttachFileIcon from '@mui/icons-material/AttachFileOutlined'
import Button from '@mui/material/Button'
import DeleteIcon from '@mui/icons-material/DeleteOutline'

interface AttachFileProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  onChange(file: File): void
  maxFileSize?: number
}

const AttachFile: FunctionComponent<AttachFileProps> = function ({ onChange, children, maxFileSize, ...props }) {
  const { t } = useTranslation(['main'])
  const fileRef = useRef(null);
  const [file, setFile] = useState<File>()

  const handleClick = useCallback(() => {
    if (file) {
      setFile(null);
      fileRef.current.value = '';
      onChange(null);
    } else {
      fileRef.current.click();
    }
  }, [file, onChange])

  const handleFileChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files[0];
    if (maxFileSize && file.size > maxFileSize) {
      alert(
        t('forms.maxFileSizeExceeded', {
          maxFileSize: approxFormatBinaryNumber(maxFileSize)
        })
      );
      fileRef.current.value = '';
      setFile(null);
      onChange(null);
      return;
    }
    setFile(file)
    onChange(file)
  }, [onChange, maxFileSize, t])

  return (
    <>
      <Button
        startIcon={file ? undefined : <AttachFileIcon />}
        endIcon={file ? <DeleteIcon /> : undefined}
        onClick={handleClick}
        sx={{ wordBreak: 'break-all' }}
        variant="text"
      >
        {file?.name ?? children}
      </Button>

      <input
        style={{ display: 'none' }}
        ref={fileRef}
        type="file"
        onChange={handleFileChange}
        {...props}
      />
    </>
  )
}

export default AttachFile;
